// ============================================================================
// FILE: ./client/src/pages/deleteAccount.jsx
// DESCRIPTION: Student account deletion with data export — Prof-iX
// ============================================================================

import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../context/provider';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomAlert from '../../components/customs/customAlert';
import useAuditLog from '../../hooks/useAuditLog';
import useStudent from '../../hooks/useStudent';
import useAuth from '../../hooks/useAuth';

import './deleteAccount.css';
import '../../styles/globalstyles.css';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const {
    authState: { authData },
  } = useContext(GlobalContext);

  const { exportActivities } = useAuditLog();
  const { deleteStudent } = useStudent();
  const { logout } = useAuth();

  const [step, setStep] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [exportSummary, setExportSummary] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [alertVisible, setAlertVisible] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);
    setError('');
    try {
      const response = await exportActivities(authData?.id);
      if (response.success) {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `profix-data-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setExportSummary({
          totalRecords: response.data.totalRecords,
          exportedAt: new Date().toLocaleDateString(),
        });
        setExportDone(true);
        setStep(3);
      } else {
        setError('Failed to export your data. Please try again.');
      }
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export your data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE exactly as shown.');
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      const response = await deleteStudent(authData.id, authData.id);
      if (response?.success !== false) {
        setStep(4);
        setTimeout(async () => {
          await logout();
          navigate('/');
        }, 30000);
      } else {
        setError(response?.message || 'Failed to delete account. Please try again.');
      }
    } catch (err) {
      console.error('Deletion failed:', err);
      setError('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="da-page">

      {/* Header */}
      <div className="da-header-row">
        <ProfileHeader data={authData} activePage="delete-account" showBackArrow={true} />
        <div className="da-logo-wrap">
          <img src="/images/prof_ix.png" alt="Prof-iX" className="da-logo" />
        </div>
        <SharedHeader newMessageNo={null} icons={[]} />
      </div>

      {/* Scrollable Body */}
      <div className="da-body">

        {/* Step Indicator */}
        {step < 4 && (
          <div className="da-steps">
            {['Warning', 'Export', 'Confirm'].map((label, idx) => (
              <React.Fragment key={idx}>
                <div className={`da-step ${step === idx + 1 ? 'da-step-active' : ''} ${step > idx + 1 ? 'da-step-done' : ''}`}>
                  <div className="da-step-circle">
                    {step > idx + 1 ? '✓' : idx + 1}
                  </div>
                  <span className="da-step-label">{label}</span>
                </div>
                {idx < 2 && (
                  <div className={`da-step-line ${step > idx + 1 ? 'da-step-line-done' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ── Step 1: Warning ── */}
        {step === 1 && (
          <div className="da-card da-animate">
            <div className="da-card-title-block">
              <span className="da-card-icon">⚠️</span>
              <h2 className="da-card-title">Delete Your Account</h2>
              <p className="da-card-subtitle">Please read carefully before proceeding</p>
            </div>

            <div className="da-box da-box-warning">
              <h3>🗑️ What will be permanently deleted:</h3>
              <ul>
                <li>Your profile and personal information</li>
                <li>All course enrollments and progress</li>
                <li>All lab, quiz, exam and capstone submissions</li>
                <li>Your grades and certificates</li>
                <li>Payment history and subscriptions</li>
                <li>Simulator sessions and circuit designs</li>
              </ul>
            </div>

            <div className="da-box da-box-info">
              <h3>✅ What you retain access to:</h3>
              <ul>
                <li>Your activity audit log for 1 year</li>
                <li>Ability to export your data at any time</li>
                <li>You can reactivate your account within 1 year</li>
              </ul>
            </div>

            <div className="da-actions">
              <button onClick={() => navigate('/profile')} className="da-btn da-btn-cancel">← Cancel</button>
              <button onClick={() => setStep(2)} className="da-btn da-btn-danger">Continue →</button>
            </div>
          </div>
        )}

        {/* ── Step 2: Export Data ── */}
        {step === 2 && (
          <div className="da-card da-animate">
            <div className="da-card-title-block">
              <span className="da-card-icon">📥</span>
              <h2 className="da-card-title">Export Your Data</h2>
              <p className="da-card-subtitle">Download a copy of your data before deleting</p>
            </div>

            <div className="da-box da-box-info">
              <h3>📦 Your export will include:</h3>
              <ul>
                <li>Complete activity audit log</li>
                <li>All actions performed on your account</li>
                <li>Timestamps and action descriptions</li>
                <li>Export date and summary</li>
              </ul>
            </div>

            <div className="da-box da-box-note">
              <span>ℹ️</span>
              <p>Exporting is recommended but not required. You can skip this step.</p>
            </div>

            {error && <div className="da-error">{error}</div>}

            <div className="da-actions">
              <button onClick={() => setStep(1)} disabled={isExporting} className="da-btn da-btn-cancel">← Back</button>
              <button onClick={() => setStep(3)} disabled={isExporting} className="da-btn da-btn-outline">Skip Export</button>
              <button onClick={handleExportData} disabled={isExporting} className="da-btn da-btn-primary">
                {isExporting ? <><span className="da-spinner" /> Exporting...</> : '📥 Export & Continue'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Confirm Deletion ── */}
        {step === 3 && (
          <div className="da-card da-animate">
            <div className="da-card-title-block">
              <span className="da-card-icon">🔐</span>
              <h2 className="da-card-title">Confirm Deletion</h2>
              <p className="da-card-subtitle">
                {exportDone ? 'Your data has been exported successfully.' : 'You chose to skip the data export.'}
              </p>
            </div>

            {exportDone && exportSummary && (
              <div className="da-box da-box-success">
                <h3>✅ Export Complete</h3>
                <p>File: <strong>profix-data-export-{new Date().toISOString().split('T')[0]}.json</strong></p>
                <ul>
                  <li>Total activity records: {exportSummary.totalRecords}</li>
                  <li>Exported on: {exportSummary.exportedAt}</li>
                </ul>
              </div>
            )}

            <div className="da-field">
              <label className="da-label">
                🧩 Reason for deletion <span className="da-label-optional">(optional)</span>
              </label>
              <div className="da-textarea-wrap">
                <textarea
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  placeholder="Tell us why you're leaving — this helps us improve Prof-iX..."
                  maxLength={200}
                  rows={3}
                  className="da-textarea"
                />
                <span className="da-char-count">{deletionReason.length}/200</span>
              </div>
            </div>

            <div className="da-field">
              <label className="da-label">
                To confirm, type <code className="da-code">DELETE</code> below:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE here..."
                className={`da-input ${confirmText && confirmText !== 'DELETE' ? 'da-input-error' : ''} ${confirmText === 'DELETE' ? 'da-input-valid' : ''}`}
              />
              {confirmText && confirmText !== 'DELETE' && (
                <span className="da-field-hint">Must match exactly: DELETE</span>
              )}
            </div>

            {error && <div className="da-error">{error}</div>}

            <div className="da-actions">
              <button onClick={() => setStep(2)} disabled={isDeleting} className="da-btn da-btn-cancel">← Back</button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting || confirmText !== 'DELETE'}
                className="da-btn da-btn-danger"
              >
                {isDeleting ? <><span className="da-spinner" /> Deleting...</> : '🗑️ Delete My Account'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Complete ── */}
        {step === 4 && (
          <div className="da-card da-animate">
            <div className="da-card-title-block">
              <span className="da-card-icon da-icon-pop">✅</span>
              <h2 className="da-card-title da-title-success">Account Deleted</h2>
              <p className="da-card-subtitle">Your account has been successfully deleted.</p>
            </div>

            <div className="da-box da-box-success">
              <h3>📋 What happens next:</h3>
              <ul>
                <li>✅ Your profile and course data have been removed</li>
                <li>✅ All submissions and grades have been deleted</li>
                <li>✅ Payment records have been cleared</li>
                <li>🔐 You can still log in to view activity logs for 1 year</li>
                <li>🗑️ All remaining data will be permanently deleted after 1 year</li>
              </ul>
            </div>

            <div className="da-box da-box-info">
              <h3>💙 Thank you for using Prof-iX.</h3>
              <p>You will be logged out automatically in <strong>30 seconds</strong>.</p>
            </div>

            <div className="da-actions da-actions-center">
              <button
                onClick={async () => { await logout(); navigate('/'); }}
                className="da-btn da-btn-primary"
              >
                Logout Now
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="da-footer">
          <p>🔒 Your data and privacy are protected under PIPEDA</p>
          <p>Questions? <a href="mailto:support@profix.com">Contact Support</a></p>
        </div>

      </div>

      <CustomAlert visible={alertVisible} onClose={() => setAlertVisible(false)} />
    </div>
  );
};

export default DeleteAccount;
